import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import TrainingRecordEditor from './TrainingRecordEditor';
import './TrainingAssessment.css';
import {
  fetchSyllabusExercises,
  getTrainingExerciseReferenceDetails,
  getTrainingAssessmentHistory,
  presentTrainingCriterion,
  saveTrainingLessonAssessment,
} from './trainingLessonAssessment';

export default function InstructorLessonAssessment({
  booking,
  onCancel,
  onSaved,
}) {
  const session = useSession();
  const profile = session?.profile;
  const briefingData = booking?.briefing_data;
  const history = getTrainingAssessmentHistory(briefingData) || [];
  const latestAssessment = history[history.length - 1] || null;
  const [syllabus, setSyllabus] = useState({
    syllabusVersionId: null,
    exercises: [],
  });
  const [loading, setLoading] = useState(Boolean(booking?.course_enrolment_id));
  const [loadError, setLoadError] = useState('');
  const [outcomes, setOutcomes] = useState(() => Object.fromEntries(
    (latestAssessment?.exerciseAssessments || []).map((item) => [
      String(item.itemId),
      item.outcome,
    ])
  ));
  const [overallOutcome, setOverallOutcome] = useState(
    latestAssessment?.overallOutcome || ''
  );
  const [lessonNotes, setLessonNotes] = useState(
    latestAssessment?.lessonNotes || ''
  );
  const [nextLessonFocus, setNextLessonFocus] = useState(
    latestAssessment?.nextLessonFocus || ''
  );
  const [correctionOfAssessedAt, setCorrectionOfAssessedAt] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveComplete, setSaveComplete] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseStatusFilter, setExerciseStatusFilter] = useState('all');
  const [expandedExercises, setExpandedExercises] = useState({});

  useEffect(() => {
    const currentHistory = getTrainingAssessmentHistory(booking?.briefing_data);
    const currentAssessment = currentHistory[currentHistory.length - 1] || null;
    setOutcomes(Object.fromEntries(
      (currentAssessment?.exerciseAssessments || []).map((item) => [
        String(item.itemId),
        item.outcome,
      ])
    ));
    setOverallOutcome(currentAssessment?.overallOutcome || '');
    setLessonNotes(currentAssessment?.lessonNotes || '');
    setNextLessonFocus(currentAssessment?.nextLessonFocus || '');
    setCorrectionOfAssessedAt('');
    setCorrectionReason('');
    setSaveError('');
    setSaveComplete(false);
  }, [booking?.id, booking?.briefing_data]);

  useEffect(() => {
    let cancelled = false;

    async function loadSyllabus() {
      if (!booking?.course_enrolment_id) {
        setLoadError('');
        setLoading(false);
        setSyllabus({ syllabusVersionId: null, exercises: [] });
        return;
      }

      setLoading(true);
      setLoadError('');
      try {
        const result = await fetchSyllabusExercises(
          supabase,
          booking.course_enrolment_id,
          booking.organisation_id
        );
        if (cancelled) return;
        if (result.error) {
          setSyllabus({ syllabusVersionId: null, exercises: [] });
          setLoadError(
            `Could not load the assigned course syllabus. ${result.error.message || 'Please try again.'}`
          );
        } else {
          setSyllabus(result.data);
        }
      } catch (error) {
        if (cancelled) return;
        setSyllabus({ syllabusVersionId: null, exercises: [] });
        setLoadError(
          `Could not load the assigned course syllabus. ${error?.message || 'Please try again.'}`
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadSyllabus();

    return () => {
      cancelled = true;
    };
  }, [booking?.course_enrolment_id, booking?.organisation_id]);

  const syllabusItems = useMemo(
    () => syllabus.exercises.flatMap((exercise) =>
      (exercise.items || []).map((item) => ({
        ...item,
        exerciseId: exercise.id,
        exerciseReference: exercise.exercise_reference,
        exerciseTitle: exercise.title,
      }))
    ),
    [syllabus.exercises]
  );
  const visibleExercises = useMemo(() => {
    const query = exerciseSearch.trim().toLowerCase();
    return syllabus.exercises.map((exercise) => {
      const items = (exercise.items || []).filter((item) => {
        const outcome = outcomes[item.id] || '';
        const statusMatches = exerciseStatusFilter === 'all' ||
          (exerciseStatusFilter === 'not_assessed'
            ? !outcome
            : outcome === exerciseStatusFilter);
        const searchMatches = !query || [
          exercise.exercise_reference,
          exercise.title,
          item.item_reference,
          item.description,
        ].some((value) => String(value || '').toLowerCase().includes(query));
        return statusMatches && searchMatches;
      });
      const exerciseMatches = !query || [
        exercise.exercise_reference,
        exercise.title,
      ].some((value) => String(value || '').toLowerCase().includes(query));
      const counts = (exercise.items || []).reduce((result, item) => {
        if (outcomes[item.id] === 'satisfactory') result.satisfactory += 1;
        else if (outcomes[item.id] === 'needs_practice') result.needsPractice += 1;
        else result.notAssessed += 1;
        return result;
      }, { satisfactory: 0, needsPractice: 0, notAssessed: 0 });
      return { ...exercise, visibleItems: items, counts, matches: exerciseMatches || items.length > 0 };
    }).filter((exercise) => exercise.matches);
  }, [exerciseSearch, exerciseStatusFilter, outcomes, syllabus.exercises]);
  const correctedAssessmentTimes = new Set(
    history.map((assessment) => assessment.correctionOfAssessedAt).filter(Boolean)
  );
  const correctionCandidates = history.filter(
    (assessment) =>
      assessment.assessedAt &&
      !correctedAssessmentTimes.has(assessment.assessedAt)
  );

  async function handleSave(event) {
    event.preventDefault();
    if (
      saving ||
      loading ||
      loadError ||
      !booking?.course_enrolment_id ||
      !profile?.id
    ) return;

    const exerciseAssessments = syllabusItems
      .filter((item) => outcomes[item.id])
      .map((item) => ({
        exerciseId: item.exerciseId,
        exerciseReference: item.exerciseReference,
        exerciseTitle: item.exerciseTitle,
        itemId: item.id,
        itemReference: item.item_reference,
        description: item.description,
        outcome: outcomes[item.id],
      }));

    setSaving(true);
    setSaveError('');
    setSaveComplete(false);

    try {
      const result = await saveTrainingLessonAssessment({
        supabase,
        booking: {
          ...booking,
          syllabusVersionId: syllabus.syllabusVersionId,
        },
        instructorProfile: profile,
        exerciseAssessments,
        overallOutcome,
        lessonNotes,
        nextLessonFocus,
        correctionOfAssessedAt,
        correctionReason,
      });

      if (!result.ok) {
        setSaveError(
          result.error?.message || 'Could not save the lesson assessment.'
        );
        return;
      }

      setSaveComplete(true);
      onSaved?.(result);
    } catch (error) {
      setSaveError(
        `Could not save the lesson assessment. ${error?.message || 'Please try again.'}`
      );
    } finally {
      setSaving(false);
    }
  }

  const hasAssessmentContent = Boolean(overallOutcome) && (
    Object.values(outcomes).some(Boolean) ||
    Boolean(lessonNotes.trim()) ||
    Boolean(nextLessonFocus.trim())
  );

  return (
    <section className="training-assessment" style={styles.panel} aria-labelledby="lesson-assessment-heading">
      <h3 id="lesson-assessment-heading" style={styles.heading}>
        Instructor lesson assessment
      </h3>
      <dl className="training-assessment-booking">
        <div><dt>Student</dt><dd>{booking?.studentName || booking?.pilotName || booking?.pilot_name || booking?.student_name || 'Student account'}</dd></div>
        <div><dt>Course</dt><dd>{booking?.courseLabel || booking?.course_name || booking?.courseName || 'Course linked to this enrolment'}</dd></div>
        <div><dt>Course enrolment</dt><dd>{booking?.course_enrolment_id || 'Not linked'}</dd></div>
        <div><dt>Booking</dt><dd>{booking?.id || 'Not available'} · {booking?.date || 'Date not set'} {booking?.time || ''}</dd></div>
        <div><dt>Syllabus version</dt><dd>{syllabus.syllabusVersionCode || syllabus.syllabusVersionId || 'Loading assigned version'}</dd></div>
      </dl>
      <p style={styles.help}>
        Each assessment is appended to this training booking. Earlier assessments
        remain in the audit history and are never overwritten.
      </p>

      {loading ? (
        <div role="status" style={styles.message}>Loading the assigned syllabus…</div>
      ) : null}

      {loadError ? (
        <div role="alert" style={styles.error}>{loadError}</div>
      ) : null}

      {!booking?.course_enrolment_id && (
        <div style={styles.message}>
          An active course enrolment is required to record an assessment. Link
          this booking to a published syllabus enrolment first.
        </div>
      )}

      {!loading && !loadError && booking?.course_enrolment_id &&
        syllabusItems.length === 0 && (
          <div style={styles.message}>
            No exercise items are published for this syllabus version. Lesson
            notes can still be recorded.
          </div>
        )}

      {syllabus.sourceReference ? (
        <details className="training-syllabus-reference">
          <summary>Syllabus source reference</summary>
          <p>{syllabus.sourceReference}</p>
        </details>
      ) : null}

      <form onSubmit={handleSave}>
        {correctionCandidates.length > 0 ? (
          <label style={styles.field}>
            <span>Correction of an earlier assessment (optional)</span>
            <select
              aria-label="Correction of earlier lesson assessment"
              value={correctionOfAssessedAt}
              onChange={(event) => {
                const selectedTimestamp = event.target.value;
                setCorrectionOfAssessedAt(selectedTimestamp);
                setCorrectionReason('');
                const target = correctionCandidates.find(
                  (assessment) => assessment.assessedAt === selectedTimestamp
                );
                if (!target) return;
                setOutcomes(Object.fromEntries(
                  (target.exerciseAssessments || []).map((item) => [
                    String(item.itemId),
                    item.outcome,
                  ])
                ));
                setOverallOutcome(target.overallOutcome || '');
                setLessonNotes(target.lessonNotes || '');
                setNextLessonFocus(target.nextLessonFocus || '');
              }}
              style={styles.select}
            >
              <option value="">Record a new assessment</option>
              {correctionCandidates.map((assessment) => (
                <option key={assessment.assessedAt} value={assessment.assessedAt}>
                  {new Date(assessment.assessedAt).toLocaleString()} · {assessment.overallOutcome || 'assessment'}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {correctionOfAssessedAt ? (
          <label style={styles.field}>
            <span>Correction reason</span>
            <textarea
              aria-label="Lesson correction reason"
              value={correctionReason}
              onChange={(event) => setCorrectionReason(event.target.value)}
              rows={2}
              required
              style={styles.textarea}
            />
          </label>
        ) : null}
        {!loading && syllabusItems.length > 0 ? (
          <section className="training-exercise-picker" aria-label="Syllabus exercise assessment">
            <div className="training-exercise-filters">
              <label style={styles.field}>
                <span>Find an exercise or criterion</span>
                <input
                  type="search"
                  aria-label="Search assessment exercises and criteria"
                  value={exerciseSearch}
                  onChange={(event) => setExerciseSearch(event.target.value)}
                  placeholder="Exercise, number or criterion"
                  style={styles.select}
                />
              </label>
              <label style={styles.field}>
                <span>Filter criteria</span>
                <select
                  aria-label="Filter assessment criteria by status"
                  value={exerciseStatusFilter}
                  onChange={(event) => setExerciseStatusFilter(event.target.value)}
                  style={styles.select}
                >
                  <option value="all">All statuses</option>
                  <option value="satisfactory">Satisfactory</option>
                  <option value="needs_practice">Needs practice</option>
                  <option value="not_assessed">Not assessed</option>
                </select>
              </label>
            </div>
            {visibleExercises.length === 0 ? (
              <div className="training-assessment-message" role="status">
                No exercises or criteria match these search and status filters.
              </div>
            ) : visibleExercises.map((exercise) => {
              const expanded = Object.prototype.hasOwnProperty.call(
                expandedExercises,
                String(exercise.id)
              ) ? expandedExercises[String(exercise.id)] : false;
              const referenceDetails = getTrainingExerciseReferenceDetails(exercise.items);
              return (
                <details
                  key={exercise.id}
                  className="training-exercise"
                  open={expanded}
                  onToggle={(event) => {
                    const isOpen = event.currentTarget.open;
                    setExpandedExercises((current) => (
                      current[String(exercise.id)] === isOpen
                        ? current
                        : { ...current, [String(exercise.id)]: isOpen }
                    ));
                  }}
                >
                  <summary>
                    <span className="training-exercise-number">{exercise.exercise_reference || '—'}</span>
                    <span className="training-exercise-title">
                      <strong>{exercise.title}</strong>
                      <span>{(exercise.items || []).length} criteria</span>
                    </span>
                    <span className="training-exercise-counts">
                      <span>{exercise.counts.satisfactory} satisfactory</span>
                      <span>{exercise.counts.needsPractice} needs practice</span>
                      <span>{exercise.counts.notAssessed} not assessed</span>
                    </span>
                    <span className="training-exercise-chevron" aria-hidden="true">{expanded ? '−' : '+'}</span>
                  </summary>
                  <div className="training-exercise-items">
                    {referenceDetails.tags.length > 0 || referenceDetails.sourceMappings.length > 0 ? (
                      <details className="training-exercise-reference">
                        <summary>Exercise references and notes</summary>
                        {referenceDetails.sourceMappings.map((reference) => (
                          <p key={reference}><strong>CAA reference:</strong> {reference}</p>
                        ))}
                        {referenceDetails.tags.length > 0 ? (
                          <p><strong>Criterion notes:</strong> {referenceDetails.tags.join(' · ')}</p>
                        ) : null}
                      </details>
                    ) : null}
                    {exercise.visibleItems.length === 0 ? (
                      <p className="training-assessment-message">No criteria in this exercise match the selected filters.</p>
                    ) : exercise.visibleItems.map((item) => (
                      <label key={item.id} className="training-assessment-item" style={styles.item}>
                        <span className="training-assessment-criterion">
                          <strong>{item.item_reference || 'Criterion'}</strong>
                          <span>{presentTrainingCriterion(item.description).text}</span>
                        </span>
                        <select
                          aria-label={`${item.item_reference || item.description} assessment`}
                          value={outcomes[item.id] || ''}
                          onChange={(event) => {
                            setOutcomes((current) => ({
                              ...current,
                              [item.id]: event.target.value,
                            }));
                            setSaveComplete(false);
                          }}
                          style={styles.select}
                        >
                          <option value="">Not assessed</option>
                          <option value="satisfactory">Satisfactory</option>
                          <option value="needs_practice">Needs practice</option>
                        </select>
                      </label>
                    ))}
                  </div>
                </details>
              );
            })}
          </section>
        ) : null}

        <label style={styles.field}>
          <span>Overall lesson outcome</span>
          <select
            aria-label="Overall lesson outcome"
            value={overallOutcome}
            required
            onChange={(event) => {
              setOverallOutcome(event.target.value);
              setSaveComplete(false);
            }}
            style={styles.select}
          >
            <option value="">Select an outcome</option>
            <option value="satisfactory">Satisfactory</option>
            <option value="needs_practice">Needs practice</option>
          </select>
        </label>

        <section className="training-assessment-review" aria-label="Assessment review before saving">
          <strong>Review before saving</strong>
          <span>
            {Object.values(outcomes).filter((outcome) => outcome === 'satisfactory').length} satisfactory ·{' '}
            {Object.values(outcomes).filter((outcome) => outcome === 'needs_practice').length} needs practice ·{' '}
            {Math.max(syllabusItems.length - Object.values(outcomes).filter(Boolean).length, 0)} not assessed
          </span>
          <span>Overall outcome: {overallOutcome || 'Not selected'}</span>
          {lessonNotes.trim() ? <span>Lesson notes: {lessonNotes}</span> : <span>No lesson notes added</span>}
          {nextLessonFocus.trim() ? <span>Next lesson focus: {nextLessonFocus}</span> : null}
          {correctionOfAssessedAt ? <span>This will be appended as a correction with the original preserved.</span> : null}
        </section>

        <label style={styles.field}>
          <span>Lesson notes</span>
          <textarea
            value={lessonNotes}
            onChange={(event) => {
              setLessonNotes(event.target.value);
              setSaveComplete(false);
            }}
            rows={3}
            style={styles.textarea}
          />
        </label>

        <label style={styles.field}>
          <span>Next lesson focus</span>
          <textarea
            value={nextLessonFocus}
            onChange={(event) => {
              setNextLessonFocus(event.target.value);
              setSaveComplete(false);
            }}
            rows={2}
            style={styles.textarea}
          />
        </label>

        {saveError ? <div role="alert" style={styles.error}>{saveError}</div> : null}
        {saveComplete ? (
          <div role="status" style={styles.success}>Lesson assessment saved.</div>
        ) : null}

        <div style={styles.actions}>
          <button
            type="submit"
            disabled={
              loading ||
              Boolean(loadError) ||
              saving ||
              !booking?.course_enrolment_id ||
              !profile?.id ||
              !hasAssessmentContent ||
              (correctionOfAssessedAt && !correctionReason.trim())
            }
            style={styles.primaryButton}
          >
            {saving ? 'Saving…' : 'Save assessment'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={styles.secondaryButton}
          >
            Cancel
          </button>
        </div>
      </form>
      {booking?.course_enrolment_id ? (
        <TrainingRecordEditor
          booking={booking}
          exercises={syllabus.exercises}
        />
      ) : null}
    </section>
  );
}

const styles = {
  panel: {
    display: 'grid',
    gap: '0.75rem',
    marginTop: '0.8rem',
    minWidth: 0,
    padding: '1rem',
    border: '1px solid rgba(120, 180, 255, 0.22)',
    borderRadius: 10,
    background: '#111c2e',
  },
  heading: {
    margin: 0,
    color: '#eff6ff',
    fontSize: '1.05rem',
    fontWeight: 700,
  },
  help: {
    margin: 0,
    color: '#aebfd2',
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },
  exercise: {
    display: 'grid',
    gap: '0.6rem',
    margin: 0,
    padding: '0.7rem',
    border: '1px solid rgba(120, 180, 255, 0.2)',
    borderRadius: 8,
  },
  legend: {
    padding: '0 0.25rem',
    color: '#dce9f7',
    fontWeight: 800,
  },
  item: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(9rem, 0.45fr)',
    gap: '0.7rem',
    alignItems: 'center',
    color: '#d6e2f0',
    fontSize: '0.84rem',
  },
  field: {
    display: 'grid',
    gap: '0.35rem',
    color: '#c8d7e8',
    fontSize: '0.84rem',
    fontWeight: 600,
  },
  select: {
    width: '100%',
    minWidth: 0,
    minHeight: 38,
    padding: '0.48rem 0.62rem',
    border: '1px solid #435a75',
    borderRadius: 6,
    background: '#101a2a',
    color: '#f2f7fc',
    font: 'inherit',
  },
  textarea: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '0.6rem',
    border: '1px solid #435a75',
    borderRadius: 6,
    background: '#101a2a',
    color: '#f2f7fc',
    font: 'inherit',
    resize: 'vertical',
  },
  message: {
    color: '#c7d8e8',
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },
  error: {
    padding: '0.7rem 0.85rem',
    borderRadius: 8,
    border: '1px solid #98545b',
    background: '#3c2229',
    color: '#ffd9dc',
    fontSize: '0.82rem',
  },
  success: {
    padding: '0.7rem 0.85rem',
    border: '1px solid #2d806c',
    borderRadius: 7,
    background: '#12392f',
    color: '#c5f2df',
    fontSize: '0.84rem',
    fontWeight: 700,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.55rem',
  },
  primaryButton: {
    minHeight: 38,
    border: '1px solid #1581aa',
    borderRadius: 7,
    padding: '0.55rem 0.85rem',
    background: '#08799f',
    color: '#eaf6ff',
    font: 'inherit',
    fontSize: '0.84rem',
    fontWeight: 650,
    cursor: 'pointer',
  },
  secondaryButton: {
    minHeight: 38,
    border: '1px solid #526982',
    borderRadius: 7,
    padding: '0.55rem 0.85rem',
    background: '#24384f',
    color: '#eaf2fc',
    font: 'inherit',
    fontSize: '0.84rem',
    fontWeight: 650,
    cursor: 'pointer',
  },
};
